/*
 * MinesGen — boards.
 *
 *   MinesGen.classic(w, h, mines, first, seed)       a random board; the first cell is an opening
 *   MinesGen.noGuess(w, h, mines, first, seed, opts) a board that pure logic solves from the first
 *                                                    click: {mine, ok, attempts, repairs, tier}
 *   MinesGen.poolBoard(w, h, mines, seed)            a random board with every first cell from which
 *                                                    it is no-guess: boards made BEFORE the click
 *   MinesGen.fromPool(board, first)                  that board turned so the click lands on such a cell
 *
 * A board is a function of its seed: the same seed and first cell give the
 * same mines in every browser (GameKit.rng — integers only). The board of the
 * day and the pinned vectors in tests/js/minesweeper-generator.test.mjs depend
 * on it: a change to the order of draws here needs a new format, not an edit.
 *
 * A classic script without DOM. Decisions and measurements — docs/why/minesweeper.md.
 */
(function (root) {
    'use strict';

    var Core = root.MinesCore, Solver = root.MinesSolver;
    function kit() { return root.GameKit; }

    // Random boards tried as they are before a stuck one is repaired. On the
    // three classic levels a plain random board is no-guess often enough
    // (measured in docs/why/minesweeper.md) that the repair is practically
    // never reached — their boards stay an unbiased sample of "random boards
    // that need no guess".
    var PLAIN_ATTEMPTS = 60;
    var MAX_ATTEMPTS = 400;
    // Above this share of mines a no-guess board is not promised: the repair
    // still finds one most of the time up to ~40 %, but no longer within the
    // time a click may take (measured in docs/why/minesweeper.md). A board
    // larger than the largest classic one gets a lower bar for the same reason.
    var NG_DENSITY = 0.3, NG_DENSITY_LARGE = 0.25, LARGE_CELLS = 720;
    // A sparse board is tried as it comes; a dense one goes to repair almost at once.
    var PLAIN_DENSITY = 0.22;

    function noGuessLimit(w, h) {
        var share = w * h > LARGE_CELLS ? NG_DENSITY_LARGE : NG_DENSITY;
        return Math.max(1, Math.min(Core.maxMines(w, h), Math.floor(w * h * share)));
    }
    function plainAttemptsFor(w, h, count) {
        return count / (w * h) <= PLAIN_DENSITY ? PLAIN_ATTEMPTS : 6;
    }

    function attemptSeed(seed, attempt) {
        return ((seed >>> 0) ^ Math.imul(attempt + 1, 0x9e3779b1)) >>> 0;
    }

    function classic(w, h, count, first, seed) {
        return Core.place(w, h, count, kit().rng(seed >>> 0), first);
    }

    // Where the logic ran out, move mines so that one stuck digit becomes
    // obvious: either all its unknown neighbours are mines or none is. Mines
    // travel to (or come from) cells nobody has information about, so the
    // total stays the same and what was already deduced mostly holds; the
    // board is then solved again from scratch — a repair is a proposal, the
    // solver is the judge.
    function repair(w, h, mine, st, rng) {
        var t = Core.topology(w, h), n = t.n, nb = t.nb, i, k;
        var num = Core.numbers(w, h, mine);
        var stuck = [];
        for (i = 0; i < n; i++) {
            if (st[i] !== 1 || num[i] === 0) continue;
            var l = nb[i], u = 0;
            for (k = 0; k < l.length; k++) if (st[l[k]] === 0) u++;
            if (u) stuck.push(i);
        }
        if (!stuck.length) return false;
        var c = stuck[rng.int(stuck.length)], lc = nb[c], inU = new Uint8Array(n), U = [], minesU = 0;
        for (k = 0; k < lc.length; k++) if (st[lc[k]] === 0) { U.push(lc[k]); inU[lc[k]] = 1; if (mine[lc[k]]) minesU++; }
        // Cells outside this digit, by how little moving a mine there disturbs:
        // first the ones no open cell touches, then the rest of the unknown ones.
        var seaFree = [], seaMines = [], edgeFree = [], edgeMines = [];
        for (i = 0; i < n; i++) {
            if (st[i] !== 0 || inU[i]) continue;
            var li = nb[i], touched = false;
            for (k = 0; k < li.length; k++) if (st[li[k]] === 1) { touched = true; break; }
            if (mine[i]) (touched ? edgeMines : seaMines).push(i);
            else (touched ? edgeFree : seaFree).push(i);
        }
        var toClear = minesU, toFill = U.length - minesU;
        var canClear = seaFree.length + edgeFree.length >= toClear;
        var canFill = seaMines.length + edgeMines.length >= toFill;
        if (!canClear && !canFill) return false;
        var clear = canClear && (!canFill || toClear <= toFill);
        function take(primary, secondary) {
            var src = primary.length ? primary : secondary, j = rng.int(src.length), v = src[j];
            src[j] = src[src.length - 1];
            src.pop();
            return v;
        }
        for (k = 0; k < U.length; k++) {
            var cell = U[k];
            if (clear && mine[cell]) { mine[cell] = 0; mine[take(seaFree, edgeFree)] = 1; }
            else if (!clear && !mine[cell]) { mine[cell] = 1; mine[take(seaMines, edgeMines)] = 0; }
        }
        return true;
    }

    //   opts {plainAttempts, maxAttempts, maxRepairs, budget, expired}
    // `expired()` — a caller that would rather have a classic board than wait
    // (a custom board at the edge of what is possible). The board of the day
    // and anything else that must be the same for everyone never passes it:
    // a deadline makes the result depend on the speed of the device.
    function noGuess(w, h, count, first, seed, opts) {
        opts = opts || {};
        var plain = opts.plainAttempts == null ? plainAttemptsFor(w, h, count) : opts.plainAttempts;
        var expired = typeof opts.expired === 'function' ? opts.expired : null;
        var maxAttempts = opts.maxAttempts || MAX_ATTEMPTS;
        var maxRepairs = opts.maxRepairs == null ? Math.max(40, Math.round(count * 1.5)) : opts.maxRepairs;
        var fallback = null, repairs = 0;
        for (var attempt = 0; attempt < maxAttempts; attempt++) {
            var rng = kit().rng(attemptSeed(seed, attempt));
            var mine = Core.place(w, h, count, rng, first);
            if (!fallback) fallback = mine.slice ? mine.slice() : new Uint8Array(mine);
            var res = Solver.solve(w, h, mine, first, { budget: opts.budget });
            if (res.error) throw new Error('MinesGen: solver ' + res.error);
            if (res.solved) return { mine: mine, ok: true, attempts: attempt + 1, repairs: repairs, tier: res.tier, usedCount: res.usedCount };
            if (expired && expired()) break;
            if (attempt < plain) continue;
            for (var r = 0; r < maxRepairs; r++) {
                if (expired && expired()) break;
                if (!repair(w, h, mine, res.st, rng)) break;
                repairs++;
                res = Solver.solve(w, h, mine, first, { budget: opts.budget });
                if (res.error) throw new Error('MinesGen: solver ' + res.error);
                if (res.solved) return { mine: mine, ok: true, attempts: attempt + 1, repairs: repairs, tier: res.tier, usedCount: res.usedCount };
            }
        }
        return { mine: fallback, ok: false, attempts: attempt, repairs: repairs, tier: 0, usedCount: false };
    }

    // ── Boards made before the click ─────────────────────────────────────────
    // A random board is no-guess from SOME first cells: every blank of an
    // opening that logic can finish the board from. Such a board, mirrored and
    // (when square) turned, serves every click that lands on one of those
    // cells — and the sample is the same as generating for that click: random
    // boards that have an opening under it and need no guess from there.
    function poolBoard(w, h, count, seed) {
        var rng = kit().rng(seed >>> 0);
        var mine = Core.place(w, h, count, rng, null);
        var t = Core.topology(w, h), n = t.n, nb = t.nb, num = Core.numbers(w, h, mine);
        var starts = new Uint8Array(n), seen = new Uint8Array(n), any = 0, stack = [];
        for (var i = 0; i < n; i++) {
            if (mine[i] || num[i] !== 0 || seen[i]) continue;
            var region = [i];
            seen[i] = 1;
            stack.push(i);
            while (stack.length) {
                var z = stack.pop(), l = nb[z];
                for (var k = 0; k < l.length; k++) {
                    var c = l[k];
                    if (seen[c] || mine[c] || num[c] !== 0) continue;
                    seen[c] = 1;
                    region.push(c);
                    stack.push(c);
                }
            }
            var res = Solver.solve(w, h, mine, i, { num: num });
            if (res.error) throw new Error('MinesGen: solver ' + res.error);
            if (!res.solved) continue;
            for (var q = 0; q < region.length; q++) { starts[region[q]] = 1; any++; }
        }
        return { w: w, h: h, mine: mine, starts: starts, count: any };
    }

    // The eight symmetries of a square (four for a rectangle): where cell
    // (x, y) of the stored board lands.
    function symmetries(w, h) {
        var list = [
            function (x, y) { return [x, y]; },
            function (x, y) { return [w - 1 - x, y]; },
            function (x, y) { return [x, h - 1 - y]; },
            function (x, y) { return [w - 1 - x, h - 1 - y]; }
        ];
        if (w === h) {
            list.push(function (x, y) { return [y, x]; });
            list.push(function (x, y) { return [w - 1 - y, x]; });
            list.push(function (x, y) { return [y, h - 1 - x]; });
            list.push(function (x, y) { return [w - 1 - y, h - 1 - x]; });
        }
        return list;
    }
    // Every cell this board can serve as a first click, counting its symmetries.
    function poolCoverage(board, into) {
        var w = board.w, h = board.h, sym = symmetries(w, h);
        for (var i = 0; i < board.starts.length; i++) {
            if (!board.starts[i]) continue;
            var x = i % w, y = (i - x) / w;
            for (var s = 0; s < sym.length; s++) { var p = sym[s](x, y); into[p[1] * w + p[0]] = 1; }
        }
        return into;
    }
    function fromPool(board, first) {
        var w = board.w, h = board.h, sym = symmetries(w, h), n = w * h;
        for (var s = 0; s < sym.length; s++) {
            var f = sym[s], hit = false, i, x, y, p;
            for (i = 0; i < n && !hit; i++) {
                if (!board.starts[i]) continue;
                x = i % w; y = (i - x) / w; p = f(x, y);
                if (p[1] * w + p[0] === first) hit = true;
            }
            if (!hit) continue;
            var mine = new Uint8Array(n);
            for (i = 0; i < n; i++) {
                if (!board.mine[i]) continue;
                x = i % w; y = (i - x) / w; p = f(x, y);
                mine[p[1] * w + p[0]] = 1;
            }
            return mine;
        }
        return null;
    }

    root.MinesGen = {
        classic: classic,
        noGuess: noGuess,
        repair: repair,
        poolBoard: poolBoard,
        poolCoverage: poolCoverage,
        fromPool: fromPool,
        symmetries: symmetries,
        attemptSeed: attemptSeed,
        noGuessLimit: noGuessLimit,
        plainAttemptsFor: plainAttemptsFor,
        NG_DENSITY: NG_DENSITY,
        PLAIN_ATTEMPTS: PLAIN_ATTEMPTS,
        MAX_ATTEMPTS: MAX_ATTEMPTS
    };
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this));
